/**
 * CrudPageLayout template - standard layout for CRUD pages
 * Provides consistent structure for Create, Read, Update, Delete operations
 */

import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Button } from '../../atoms/buttons/Button';
import {
  Breadcrumb,
  BreadcrumbProps,
} from '../../molecules/navigation/Breadcrumb';
import { DataTable, DataTableProps } from '../../organisms/tables/DataTable';
import { BaseComponentProps } from '../../types';
import { AdminLayout } from './AdminLayout';

export interface CrudPageLayoutProps<T = any> extends BaseComponentProps {
  /** Use compact management-page spacing. */
  compact?: boolean;
  /** Page title */
  title: string;
  /** Page subtitle */
  subtitle?: string;
  /** Breadcrumb navigation */
  breadcrumb?: BreadcrumbProps;
  /** Table configuration */
  table: DataTableProps<T>;
  /** Create button configuration */
  createButton?: {
    label: string;
    onClick: () => void;
  };
  /** Additional header actions */
  headerActions?: React.ReactNode;
  /** Additional content above table */
  children?: React.ReactNode;
  /** Header icon */
  headerIcon?: React.ReactElement;
  /** Header badge */
  headerBadge?: {
    text: string;
    colorScheme?: string;
    variant?: 'solid' | 'outline' | 'subtle';
  };
  /** Header styling variant */
  headerVariant?: 'default' | 'elevated' | 'minimal';
}

export const CrudPageLayout = <T extends Record<string, any>>({
  title,
  compact = false,
  subtitle,
  breadcrumb,
  table,
  createButton,
  headerActions,
  children,
  headerIcon,
  headerBadge,
  headerVariant = 'default',
  className,
  testId,
}: CrudPageLayoutProps<T>) => {
  const muted = useColorModeValue('gray.600', 'gray.300');
  const headerActionsContent = (
    <>
      {createButton && (
        <Button
          size={compact ? 'sm' : 'md'}
          variant="primary"
          onClick={createButton.onClick}
        >
          {createButton.label}
        </Button>
      )}
      {headerActions}
    </>
  );

  return (
    <AdminLayout
      padding={compact ? 3 : 8}
      header={
        compact
          ? undefined
          : {
              title,
              subtitle,
              breadcrumb,
              actions: headerActionsContent,
              icon: headerIcon,
              badge: headerBadge,
              variant: headerVariant,
            }
      }
      className={className}
      data-testid={testId}
    >
      <VStack spacing={compact ? 4 : 6} align="stretch">
        {compact && (
          <Box>
            {breadcrumb && <Breadcrumb {...breadcrumb} />}
            <Flex
              justify="space-between"
              align="center"
              gap={3}
              wrap="wrap"
              mt={3}
            >
              <Box>
                <HStack spacing={2} flexWrap="wrap">
                  <Heading size="md">{title}</Heading>
                  {headerBadge && (
                    <Badge
                      colorScheme={headerBadge.colorScheme}
                      variant={headerBadge.variant}
                    >
                      {headerBadge.text}
                    </Badge>
                  )}
                </HStack>
                {subtitle && (
                  <Text fontSize="sm" color={muted} mt={1}>
                    {subtitle}
                  </Text>
                )}
              </Box>
              <HStack spacing={2} flexWrap="wrap">
                {headerActionsContent}
              </HStack>
            </Flex>
          </Box>
        )}
        {children}

        <DataTable
          compact={compact}
          size={compact ? 'sm' : 'md'}
          striped={!compact}
          {...table}
        />
      </VStack>
    </AdminLayout>
  );
};
